/**
 * Hurst Exponent (Rescaled Range estimate)
 *
 * Estimates the Hurst exponent using a simplified R/S method
 * to classify market memory:
 *   H > 0.5 = Trending (persistent)
 *   H = 0.5 = Random walk
 *   H < 0.5 = Mean-reverting (anti-persistent)
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const hurstExponentIndicator: IndicatorDefinition = {
  id: "hurst_exponent",
  name: "Hurst Exponent",
  shortName: "Hurst",
  category: "advanced",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 100, min: 20, max: 500 },
  ],
  plots: [
    { key: "hurst", label: "Hurst", color: "#7E57C2", style: "line", lineWidth: 2 },
    { key: "mid", label: "0.5 (Random)", color: "#555555", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    const closes = candles.map(c => c.close);
    const hurstData: { time: number; value: number }[] = [];
    const midData: { time: number; value: number }[] = [];

    for (let i = period; i < closes.length; i++) {
      const slice = closes.slice(i - period, i);

      // Log returns
      const returns: number[] = [];
      for (let j = 1; j < slice.length; j++) {
        returns.push(Math.log(slice[j] / slice[j - 1]));
      }

      const n = returns.length;
      if (n < 10) continue;

      // Mean of returns
      const mean = returns.reduce((a, b) => a + b, 0) / n;

      // Cumulative deviations from mean
      let cumDev = 0;
      let maxDev = -Infinity;
      let minDev = Infinity;

      for (let j = 0; j < n; j++) {
        cumDev += returns[j] - mean;
        if (cumDev > maxDev) maxDev = cumDev;
        if (cumDev < minDev) minDev = cumDev;
      }

      const R = maxDev - minDev;

      // Standard deviation
      let sumSq = 0;
      for (const r of returns) {
        sumSq += (r - mean) * (r - mean);
      }
      const S = Math.sqrt(sumSq / n);

      if (S > 0 && R > 0) {
        const RS = R / S;
        // H ≈ log(R/S) / log(n)
        const H = Math.log(RS) / Math.log(n);
        // Clamp to reasonable range
        const clampedH = Math.max(0, Math.min(1, H));
        hurstData.push({ time: candles[i].time, value: clampedH });
        midData.push({ time: candles[i].time, value: 0.5 });
      }
    }

    return [
      { key: "hurst", data: hurstData },
      { key: "mid", data: midData },
    ];
  },
};
